import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * requestAnimationFrame id of a close that is scheduled but not yet applied. Non-null means the
   * panel is still on screen, so a `pointerenter` arriving right now is a swap between top-level
   * items rather than a fresh open. See SPEC_mega-menu-open-latency.md → Fix 1.
   * @type {number | null}
   */
  #pendingClose = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#cancelPendingClose();
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   *
   * Write-only: the height comes from `#measureSafetyBoxHeight`, taken during `activate`'s read
   * phase, so that measuring it does not force another layout after the custom properties written
   * before it have already invalidated the old one.
   *
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   * @param {number} boxHeight
   */
  #startPointerTracking(item, previousItem, boxHeight) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    item.style.setProperty('--box-height', `${boxHeight}px`);
  }

  /**
   * Distance from the top of the menu item down to the bottom edge of the header: the height of
   * the invisible box that forgives a diagonal pointer path from the item into its submenu.
   * @param {HTMLElement} item
   * @returns {number}
   */
  #measureSafetyBoxHeight(item) {
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;

    return (boundary?.getBoundingClientRect().bottom ?? 0) - item.getBoundingClientRect().top;
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately.
   *
   * Ordered as one write, then every read, then every write. Interleaving them made the browser
   * run layout three times per hover — 33ms in a DevTools trace, 17ms averaged over the eight
   * submenus, against ~6ms batched. The leading write is the one that cannot be moved:
   * `[data-active]` flips the submenu from `content-visibility: auto` to `visible`
   * (sections/header.liquid), and without it `offsetHeight` answers with the
   * `contain-intrinsic-size` placeholder instead of the real height.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    // The pointer left one item and arrived on another — or came back to the same one — inside a
    // single task. Either way the panel must stay put, so drop the close `#deactivate` queued.
    // This has to happen before the early return below, or returning to the current item would
    // still let the queued close fire.
    this.#cancelPendingClose();

    if (item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    const previouslyActiveItem = this.#state.activeItem;
    // A panel is already on screen, so its content has to be replaced within one frame: cross-fading
    // it leaves the shopper looking at an open, empty panel for the length of the fade.
    const swapping = previouslyActiveItem !== null;

    const previousSubmenu = previouslyActiveItem ? findSubmenu(previouslyActiveItem) : null;

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      this.#observeDeferredSubmenuContent(submenu);
    }

    /* ——— reads ——— */

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#measureHeaderVisibleHeight();
    const boxHeight = this.#measureSafetyBoxHeight(item);

    /* ——— writes ——— */

    this.toggleAttribute('data-swapping', swapping);
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    // On a swap nothing else clears this: `#deactivate` is the only other place that does, and it
    // was cancelled above. Left behind, it pins the outgoing submenu at `content-visibility:
    // visible` for the rest of the page's life — one hover across the bar and the optimisation is
    // gone for every menu the pointer touched.
    if (previousSubmenu) {
      delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.#startPointerTracking(item, previouslyActiveItem, boxHeight);
  };

  /**
   * Watch for submenu markup that arrives late — the section-hydration morph
   * (assets/section-hydration.js) injects it into an already-rendered page. Once the section
   * reports itself hydrated nothing writes into a submenu again, so the observer and its timer are
   * skipped rather than built and torn down on every single hover.
   *
   * @param {HTMLElement} submenu
   */
  #observeDeferredSubmenuContent(submenu) {
    const section = /** @type {HTMLElement | null} */ (this.closest('.shopify-section'));

    if (section?.dataset.hydrated === 'true') return;

    // Cleanup any existing mutation observer from previous menu activations
    this.#cleanupMutationObserver();

    // Monitor DOM mutations to catch deferred content injection (from section hydration)
    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
            this.#cleanupMutationObserver();
          }
        });
      });
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

    // Auto-disconnect after 500ms to prevent memory leaks
    setTimeout(() => {
      this.#cleanupMutationObserver();
    }, 500);
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Schedule the panel to close on the next frame.
   *
   * The frame of slack is the whole point: when the pointer crosses from one top-level item to
   * another, the browser dispatches `pointerleave` and `pointerenter` in the same task, and
   * `activate` cancels this close before it ever runs. Closing synchronously wrote the collapsed
   * values and `activate` wrote them straight back, so the style system never observed the closed
   * state — no transition restarted, and the shopper was left looking at an open, empty panel
   * while the incoming content cross-faded in. See SPEC_mega-menu-open-latency.md → Fix 1.
   *
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    if (this.#pendingClose !== null) return;

    this.#pendingClose = requestAnimationFrame(() => {
      this.#pendingClose = null;
      this.#applyClose(item);
    });
  };

  /**
   * Collapse the panel. Only reached a frame after `#deactivate`, and only while the item it was
   * queued for is still the active one.
   * @param {HTMLElement} item
   */
  #applyClose(item) {
    if (item !== this.#state.activeItem) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.dataset.overflowExpanded = 'false';
    this.removeAttribute('data-swapping');

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  }

  /**
   * Drop a close that has been scheduled but not yet applied.
   * @returns {boolean} Whether one was pending.
   */
  #cancelPendingClose() {
    if (this.#pendingClose === null) return false;

    cancelAnimationFrame(this.#pendingClose);
    this.#pendingClose = null;

    return true;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Height of the header as it is drawn while a submenu is open: the top row alone when the
   * submenu overlaps the bottom row, the whole header otherwise.
   *
   * Read-only, so `activate` can take this measurement alongside the others, before it starts
   * writing custom properties.
   *
   * @returns {number}
   */
  #measureHeaderVisibleHeight() {
    const header = this.headerComponent;

    if (!header) return 0;

    const isOverlapSituation = header.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && header.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (header.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : header.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} [headerVisibleHeight] - From `#measureHeaderVisibleHeight`; irrelevant when closing.
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight = 0) {
    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
